<template>
  <section class="qr-login-page">
    <header class="qr-login-bar">
      <button type="button" :aria-label="$t('qrLogin.back')" @click="close"><i class="el-icon-arrow-left" /></button>
      <strong>{{ $t("qrLogin.title") }}</strong>
      <span />
    </header>

    <main class="qr-login-scroll">
      <section v-if="stage === 'scan'" class="qr-login-hero">
        <small>{{ $t("qrLogin.eyebrow") }}</small>
        <h1>{{ $t("qrLogin.scanTitle") }}</h1>
        <p>{{ $t("qrLogin.subtitle") }}</p>

        <div :class="['qr-camera', `state-${cameraState}`]">
          <video ref="video" muted playsinline />
          <div class="qr-camera-frame"><i /><i /><i /><i /></div>
          <span v-if="cameraState !== 'active'"><i :class="cameraState === 'starting' ? 'el-icon-loading' : 'el-icon-full-screen'" /><strong>{{ cameraStatusText }}</strong></span>
          <em v-else>{{ $t("qrLogin.cameraActive") }}</em>
        </div>

        <button v-if="cameraState !== 'active' && cameraState !== 'starting'" class="qr-primary" type="button" @click="startCamera">
          <i class="el-icon-camera" />{{ $t("qrLogin.startCamera") }}
        </button>
        <button v-else-if="cameraState === 'active'" class="qr-secondary" type="button" @click="stopCamera">
          {{ $t("qrLogin.stopCamera") }}
        </button>
      </section>

      <section v-else-if="stage === 'processing'" class="qr-processing" aria-live="polite" aria-busy="true">
        <div class="qr-processing-mark" aria-hidden="true">
          <span /><span /><span />
          <i class="el-icon-full-screen" />
        </div>
        <small>{{ $t("qrLogin.processingEyebrow") }}</small>
        <h1>{{ $t("qrLogin.processingTitle") }}</h1>
        <p>{{ $t("qrLogin.processingTip") }}</p>
        <div class="qr-processing-dots" aria-hidden="true"><i /><i /><i /></div>
      </section>

      <section v-else-if="stage === 'preview' && preview" class="qr-preview">
        <div class="qr-shield"><i class="el-icon-lock" /></div>
        <small>{{ $t("qrLogin.confirmEyebrow") }}</small>
        <h1>{{ $t("qrLogin.confirmTitle") }}</h1>
        <p>{{ $t("qrLogin.confirmTip") }}</p>
        <article>
          <span><small>{{ $t("qrLogin.requestFrom") }}</small><strong>{{ preview.client_name }}</strong></span>
          <span><small>{{ $t("qrLogin.device") }}</small><strong>{{ preview.device_name }}</strong></span>
          <span><small>{{ $t("qrLogin.expires") }}</small><strong>{{ $d(preview.expires_time, { dateStyle: "medium", timeStyle: "short" }) }}</strong></span>
        </article>
        <div class="qr-security-note"><i class="el-icon-warning-outline" /><span>{{ $t("qrLogin.securityNotice") }}</span></div>
        <button class="qr-primary" type="button" :disabled="loading" @click="approve">
          <i :class="loading ? 'el-icon-loading' : 'el-icon-circle-check'" />{{ $t("qrLogin.approve") }}
        </button>
        <button class="qr-secondary" type="button" :disabled="loading" @click="deny">{{ $t("qrLogin.deny") }}</button>
      </section>

      <section v-else class="qr-result">
        <span><i class="el-icon-circle-check" /></span>
        <small>STARLIVE SECURE LOGIN</small>
        <h1>{{ $t("qrLogin.approved") }}</h1>
        <p>{{ $t("qrLogin.approvedTip") }}</p>
        <button class="qr-primary" type="button" @click="close">{{ $t("qrLogin.done") }}</button>
      </section>
    </main>
  </section>
</template>

<script>
import jsQR from "jsqr"
import { approveQrLogin, denyQrLogin, getQrLoginPreview } from "../api/qrLogin"

export default {
  name: "QrLoginScanner",
  data() {
    return {
      stage: "scan",
      cameraState: "idle",
      stream: null,
      detector: null,
      scanCanvas: null,
      scanTimer: null,
      detecting: false,
      ticket: "",
      preview: null,
      loading: false
    }
  },
  computed: {
    cameraStatusText() {
      const key = {
        starting: "qrLogin.openingCamera",
        unsupported: "qrLogin.unsupported",
        insecure: "qrLogin.insecure",
        denied: "qrLogin.permissionDenied",
        failed: "qrLogin.cameraFailed"
      }[this.cameraState] || "qrLogin.cameraReady"
      return this.$t(key)
    }
  },
  beforeDestroy() {
    this.stopCamera()
  },
  methods: {
    async startCamera() {
      if (!window.isSecureContext) {
        this.cameraState = "insecure"
        return
      }
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        this.cameraState = "unsupported"
        return
      }

      this.cameraState = "starting"
      try {
        // iOS Safari 等浏览器没有 BarcodeDetector，自动改用本地视频帧解码。
        try {
          this.detector = "BarcodeDetector" in window
            ? new window.BarcodeDetector({ formats: ["qr_code"] })
            : null
        } catch (error) {
          this.detector = null
        }
        this.stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 }
          },
          audio: false
        })
        this.$refs.video.srcObject = this.stream
        await this.$refs.video.play()
        this.cameraState = "active"
        this.scheduleScan()
      } catch (error) {
        this.cameraState = error && ["NotAllowedError", "SecurityError"].includes(error.name) ? "denied" : "failed"
        this.stopStreamTracks()
      }
    },
    scheduleScan() {
      clearTimeout(this.scanTimer)
      if (this.cameraState !== "active") return
      this.scanTimer = setTimeout(this.detectFrame, 280)
    },
    async detectFrame() {
      if (this.detecting || this.cameraState !== "active" || !this.$refs.video) return
      this.detecting = true
      try {
        let payload = ""
        if (this.detector) {
          try {
            const results = await this.detector.detect(this.$refs.video)
            payload = results && results.length ? String(results[0].rawValue || "") : ""
          } catch (error) {
            // 原生识别器运行失败后切换到 jsQR，避免后续每一帧重复抛出异常。
            this.detector = null
          }
        }
        if (!this.detector && !payload) payload = this.decodeVideoFrame(this.$refs.video)
        if (payload) {
          await this.acceptPayload(payload)
          return
        }
      } catch (error) {
        // 视频帧尚未准备好时继续下一轮，不向用户显示无意义的底层异常。
      } finally {
        this.detecting = false
      }
      this.scheduleScan()
    },
    decodeVideoFrame(video) {
      if (!video || video.readyState < 2 || !video.videoWidth || !video.videoHeight) return ""
      const maxWidth = 720
      const scale = Math.min(1, maxWidth / video.videoWidth)
      const width = Math.max(1, Math.round(video.videoWidth * scale))
      const height = Math.max(1, Math.round(video.videoHeight * scale))
      if (!this.scanCanvas) this.scanCanvas = document.createElement("canvas")
      const canvas = this.scanCanvas
      if (canvas.width !== width) canvas.width = width
      if (canvas.height !== height) canvas.height = height
      const context = canvas.getContext("2d", { willReadFrequently: true })
      if (!context) return ""
      context.drawImage(video, 0, 0, width, height)
      const frame = context.getImageData(0, 0, width, height)
      const result = jsQR(frame.data, width, height, { inversionAttempts: "attemptBoth" })
      return result && result.data ? String(result.data) : ""
    },
    async acceptPayload(payload) {
      if (this.loading || this.stage !== "scan") return
      const ticket = this.extractTicket(payload)
      if (!ticket) {
        this.$notice.warning(this.$t("qrLogin.invalidCode"))
        return
      }

      this.stopCamera()
      this.ticket = ticket
      this.stage = "processing"
      this.loading = true
      try {
        // 保留可感知的最短反馈时间；接口较慢时仍以真实请求耗时为准。
        const [response] = await Promise.all([
          getQrLoginPreview({ ticket }),
          new Promise(resolve => setTimeout(resolve, 480))
        ])
        this.preview = response.data
        this.stage = "preview"
      } catch (error) {
        // 请求层负责显示标准错误；页面回到可重试状态，不让用户停留在无限加载中。
        this.ticket = ""
        this.preview = null
        this.stage = "scan"
        this.cameraState = "idle"
      } finally {
        this.loading = false
      }
    },
    extractTicket(payload) {
      const value = String(payload || "").trim()
      if (/^[a-f0-9]{64}$/.test(value)) return value
      try {
        const url = new URL(value)
        if (!["starlive:", "https:", "http:"].includes(url.protocol)) return ""
        const ticket = String(url.searchParams.get("ticket") || url.searchParams.get("qr_login_ticket") || "").trim()
        return /^[a-f0-9]{64}$/.test(ticket) ? ticket : ""
      } catch (error) {
        return ""
      }
    },
    async approve() {
      if (!this.ticket || this.loading) return
      this.loading = true
      try {
        await approveQrLogin({ ticket: this.ticket })
        this.stage = "success"
        this.$notice.success(this.$t("qrLogin.approved"))
      } finally {
        this.loading = false
      }
    },
    async deny() {
      if (!this.ticket || this.loading) return
      this.loading = true
      try {
        await denyQrLogin({ ticket: this.ticket })
        this.$notice.info(this.$t("qrLogin.denied"))
        this.close()
      } finally {
        this.loading = false
      }
    },
    stopStreamTracks() {
      if (this.stream) this.stream.getTracks().forEach(track => track.stop())
      this.stream = null
      if (this.$refs.video) this.$refs.video.srcObject = null
    },
    stopCamera() {
      clearTimeout(this.scanTimer)
      this.scanTimer = null
      this.detecting = false
      this.detector = null
      this.stopStreamTracks()
      if (this.cameraState === "active" || this.cameraState === "starting") this.cameraState = "idle"
    },
    close() {
      this.stopCamera()
      this.$emit("close")
    }
  }
}
</script>

<style scoped>
.qr-login-page{position:absolute;inset:0;z-index:60;overflow:hidden;background:radial-gradient(circle at 90% 0,rgba(255,64,88,.18),transparent 30%),#0d0e11;color:#f7f3eb}
.qr-login-bar{height:58px;display:grid;grid-template-columns:38px minmax(0,1fr) 38px;align-items:center;padding:0 16px;border-bottom:1px solid rgba(255,255,255,.08);background:rgba(13,14,17,.88);backdrop-filter:blur(18px)}
.qr-login-bar button{width:38px;height:38px;border:0;border-radius:50%;background:#202126;color:#fff;font-size:17px}.qr-login-bar strong{text-align:center}
.qr-login-scroll{height:calc(100% - 58px);overflow-y:auto;padding:24px 18px 36px;scrollbar-width:none}.qr-login-scroll::-webkit-scrollbar{display:none}
.qr-login-hero>small,.qr-processing>small,.qr-preview>small,.qr-result>small{color:#e6bd76;font-size:9px;letter-spacing:2.1px}.qr-login-hero h1,.qr-processing h1,.qr-preview h1,.qr-result h1{margin:7px 0 8px;font-family:Georgia,"Songti SC",serif;font-size:25px}.qr-login-hero>p,.qr-processing>p,.qr-preview>p,.qr-result>p{margin:0;color:#8f9199;font-size:11px;line-height:1.65}
.qr-camera{position:relative;height:258px;margin:22px 0 14px;overflow:hidden;border:1px solid rgba(230,189,118,.2);border-radius:24px;background:radial-gradient(circle,#24262c,#111216 68%)}
.qr-camera video{width:100%;height:100%;object-fit:cover}.qr-camera>span{position:absolute;inset:0;display:grid;place-content:center;justify-items:center;gap:12px;padding:28px;color:#8d9098;text-align:center}.qr-camera>span i{font-size:30px;color:#e6bd76}.qr-camera>span strong{max-width:260px;font-size:12px;line-height:1.5}
.qr-camera>em{position:absolute;inset-inline-start:12px;bottom:11px;padding:5px 8px;border-radius:9px;background:rgba(0,0,0,.68);color:#fff;font-size:9px;font-style:normal}
.qr-camera-frame{position:absolute;inset:45px;pointer-events:none}.qr-camera-frame i{position:absolute;width:34px;height:34px;border-color:#ff5368}.qr-camera-frame i:nth-child(1){top:0;left:0;border-top:3px solid;border-left:3px solid}.qr-camera-frame i:nth-child(2){top:0;right:0;border-top:3px solid;border-right:3px solid}.qr-camera-frame i:nth-child(3){right:0;bottom:0;border-right:3px solid;border-bottom:3px solid}.qr-camera-frame i:nth-child(4){bottom:0;left:0;border-bottom:3px solid;border-left:3px solid}
.qr-primary,.qr-secondary{width:100%;min-height:45px;margin-top:10px;border-radius:14px;font-weight:700}.qr-primary{border:0;background:linear-gradient(105deg,#ff4058,#ff6f58);color:#fff}.qr-primary i{margin-inline-end:7px}.qr-secondary{border:1px solid #34363d;background:#191a1e;color:#aeb0b6}.qr-primary:disabled,.qr-secondary:disabled{opacity:.55}
.qr-processing{display:grid;justify-items:center;place-content:center;min-height:100%;padding:26px 10px;text-align:center}.qr-processing-mark{position:relative;width:108px;height:108px;display:grid;place-items:center;margin-bottom:24px;border-radius:34px;background:radial-gradient(circle at 45% 35%,rgba(255,111,88,.22),transparent 52%),#17181c;box-shadow:0 24px 64px rgba(0,0,0,.42),inset 0 0 0 1px rgba(230,189,118,.14)}.qr-processing-mark>i{color:#f0ca86;font-size:30px;animation:qr-processing-pulse 1.35s ease-in-out infinite}.qr-processing-mark>span{position:absolute;inset:9px;border:1px solid transparent;border-top-color:#ff5368;border-radius:30px;animation:qr-processing-orbit 1.2s linear infinite}.qr-processing-mark>span:nth-child(2){inset:18px;border-top-color:transparent;border-right-color:#e6bd76;animation-duration:1.7s;animation-direction:reverse}.qr-processing-mark>span:nth-child(3){inset:-8px;border-top-color:rgba(255,255,255,.13);animation-duration:2.4s}.qr-processing>p{max-width:280px}.qr-processing-dots{display:flex;gap:6px;margin-top:24px}.qr-processing-dots i{width:5px;height:5px;border-radius:50%;background:#ff5368;animation:qr-processing-dot 1.1s ease-in-out infinite}.qr-processing-dots i:nth-child(2){animation-delay:.16s}.qr-processing-dots i:nth-child(3){animation-delay:.32s}
.qr-preview,.qr-result{padding-top:26px;text-align:center}.qr-shield,.qr-result>span{width:76px;height:76px;display:grid;place-items:center;margin:0 auto 20px;border:1px solid rgba(230,189,118,.35);border-radius:24px;background:linear-gradient(145deg,#2d271d,#171719);color:#efcd8d;font-size:30px;box-shadow:0 18px 42px rgba(0,0,0,.3)}
.qr-preview article{margin:24px 0 14px;padding:7px 15px;border:1px solid #2a2b31;border-radius:18px;background:#17181c;text-align:start}.qr-preview article span{display:flex;align-items:center;justify-content:space-between;gap:12px;padding:13px 2px;border-bottom:1px solid #292a30}.qr-preview article span:last-child{border-bottom:0}.qr-preview article small{color:#777a82;font-size:9px}.qr-preview article strong{max-width:62%;font-size:11px;text-align:end;overflow-wrap:anywhere}
.qr-security-note{display:flex;align-items:flex-start;gap:9px;margin-bottom:18px;padding:12px;border-radius:13px;background:rgba(255,177,75,.07);color:#b6a68b;font-size:10px;line-height:1.55;text-align:start}.qr-security-note i{flex:none;margin-top:2px;color:#e6bd76}
.qr-result{display:grid;justify-items:center;place-content:center;min-height:100%}.qr-result>span{border-color:rgba(79,211,151,.35);background:linear-gradient(145deg,#17352b,#141b18);color:#66d6a2}.qr-result .qr-primary{min-width:220px;margin-top:24px}
[dir=rtl] .qr-security-note,[dir=rtl] .qr-preview article{text-align:right}
@keyframes qr-processing-orbit{to{transform:rotate(360deg)}}
@keyframes qr-processing-pulse{0%,100%{transform:scale(.94);opacity:.72}50%{transform:scale(1.06);opacity:1}}
@keyframes qr-processing-dot{0%,60%,100%{transform:translateY(0);opacity:.35}30%{transform:translateY(-5px);opacity:1}}
@media (prefers-reduced-motion:reduce){.qr-processing-mark>span,.qr-processing-mark>i,.qr-processing-dots i{animation:none}.qr-processing-mark>span{opacity:.72}}
</style>
